import { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { api } from '@/core/api/queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FaRotate } from 'react-icons/fa6';
import { ConfirmationDialog } from '@/shared/components/dialogs/ConfirmationDialog';
import { useConfirmationDialog } from '@/shared/hooks/useConfirmationDialog';
import { EditionSelector } from '@/shared/components/tables';
import { useEditions } from '@/shared/hooks/use-editions';
import { TierFormDialog } from '../components/TierFormDialog';
import { useTicketMutations } from '../hooks/use-ticket-mutations';
import { formatZi } from '../utils/tier-state';
import type { TicketTierDto } from '../api/tickets.api';

/**
 * Cate niveluri poate avea o editie. Era 4, e 6 din b21.
 *
 * ⚠️ Numarul asta e o LIMITA DE ADMINISTRARE, nu o descriere a datelor: azi
 * editiile reale au cinci trepte, si a sasea («Prieten Eco») e continut de
 * creat de admin, nu ceva ce exista undeva. Nu citi din el cate trepte sunt.
 *
 * 🔴 Peste sase, banda de culoare NU mai are token: `tokenBanda` are cinci
 * verzi (`--color-rang-0`..`4`) si le aliniaza la CAPATUL listei, deci a
 * saptea treapta ar primi un token repetat, tacut. Cresterea limitei de aici
 * peste 6 cere intai o decizie pe paleta, nu o cifra schimbata.
 */
const MAX_NIVELURI = 6;

/**
 * Nivelurile de tichete per editie (spec §8) - DBEST/ADMIN.
 *
 * Zero `window.confirm`: blocheaza automatizarea de browser si e inutilizabil pe mobil.
 */
export default function TicketTiersAdminPage() {
    const { editions, currentEdition, changeEdition } = useEditions();
    const editionId = currentEdition?.id;
    const tiers = api.tickets.tiers.useQuery({ variables: { editionId }, enabled: !!editionId });
    const { isError: niveluriEroare, refetch: reincearcaNiveluri } = tiers;
    const { createTier, updateTier, deleteTier } = useTicketMutations();

    // undefined = dialogul e inchis; null = nivel nou; obiect = editare.
    const [editing, setEditing] = useState<TicketTierDto | null | undefined>(undefined);
    const [deSters, setDeSters] = useState<TicketTierDto | null>(null);

    const stergere = useConfirmationDialog({
        onConfirm: async () => {
            if (deSters?.id) await deleteTier.mutateAsync(deSters.id);
            setDeSters(null);
        },
        onCancel: () => setDeSters(null),
    });

    const niveluri = [...(tiers.data ?? [])].sort((a, b) => (a.level ?? 0) - (b.level ?? 0));
    const plin = niveluri.length >= MAX_NIVELURI;

    return (
        /* SE-19: aceeasi familie ca RecipienteAdminPage.tsx (P1-3/P0-6). Shell-ul autentificat
           taie overflow-ul NECONDITIONAT (nu doar la `md:`) pe tot lantul --
           `.ecotic-main-content` si `[data-slot="sidebar-inset"]` (main.css, ambele
           `overflow: hidden` fara media query) -- iar pagina n-avea propriul scroller: pana la
           6 niveluri plus istoricul puteau iesi din viewport fara nicio cale de derulare.
           Fix pe RADACINA PROPRIE: `overflow-y-auto` + `min-h-0` (fara `min-h-0`, un flex item
           intr-un parinte flex refuza sa scada sub propriul min-content, deci `overflow-y-auto`
           n-are ce derula).

           🔴 `w-full` LIPSEA, si e chiar defectul P0-6: `<main>` e `display:flex`, deci radacina
           asta e un flex item care, fara latime declarata, se dimensioneaza dupa continut
           (435,56px intr-un viewport de 360, masurat pe SchoolPointsPage) -- adica derulare
           orizontala pe toata pagina. `max-w-*` limiteaza doar in sus; nu obliga la nimic in jos.

           🔴 Si scroller-ul e acum SEPARAT de coloana: cu `max-w-3xl` pe acelasi element cu
           `overflow-y-auto`, bara cadea la marginea coloanei, nu la marginea zonei de continut.
           `max-w-*` nu are ce cauta pe scroller. `p-4` ramane in INTERIOR, deci continutul sta la
           aceeasi pozitie pe ecran; se muta doar bara. */
        <div className="min-h-0 w-full overflow-y-auto">
            <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <h1 className="text-fluid-2xl font-bold">Niveluri de premii</h1>
                    <EditionSelector editions={editions} currentEditionId={editionId} onChange={changeEdition} />
                </div>
                <p className="text-sm text-muted-foreground">
                    Până la șase niveluri per ediție. Valorile și intervalele le stabilește ECOTIC.
                </p>

                {/* Un 500/retea NU mai cade tacut pe "zero niveluri" (precedent #191/p3-1/p3-5) —
                    starea de eroare e distincta de editia reala fara niveluri, care ramane neatinsa. */}
                {niveluriEroare ? (
                    <Alert className="border-error bg-error/10">
                        <AlertDescription>
                            <div className="text-center">
                                <p className="mb-2 font-semibold text-error">Nu am putut încărca nivelurile</p>
                                <p className="text-error">Verificați conexiunea și încercați din nou.</p>
                                <Button type="button" variant="outline" className="mt-3" onClick={() => reincearcaNiveluri()}>
                                    <FaRotate className="mr-2" />
                                    <span>Încearcă din nou</span>
                                </Button>
                            </div>
                        </AlertDescription>
                    </Alert>
                ) : (
                <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {niveluri.map((t) => (
                        <Card key={t.id} className="py-0">
                            <CardContent className="flex items-start gap-3 p-4">
                                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-lilac/30 font-bold text-magenta tabular-nums">
                                    {t.level}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="line-clamp-2 font-semibold">{t.name}</p>
                                    <p className="text-sm text-muted-foreground tabular-nums">
                                        {t.pointsCost} p · {t.couponValue} lei
                                        {t.maxPerSchool ? ` · max ${t.maxPerSchool}/școală` : ''}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {formatZi(t.availableFrom as unknown as string) ?? '—'}
                                        {' → '}
                                        {formatZi(t.availableTo as unknown as string) ?? '—'}
                                    </p>
                                </div>
                                {/* Orizontal, nu stivuit: stivuite fortau cardul la 174px, din care ~92
                                    erau doar coloana de butoane. Pe 4 carduri, un sfert din viewport. */}
                                <div className="flex shrink-0 gap-1">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="touch-target"
                                        aria-label={`Editează nivelul ${t.name}`}
                                        onClick={() => setEditing(t)}
                                    >
                                        <Pencil />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="touch-target text-destructive"
                                        aria-label={`Șterge nivelul ${t.name}`}
                                        onClick={() => { setDeSters(t); stergere.show(); }}
                                    >
                                        <Trash2 />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {niveluri.length === 0 && !tiers.isLoading && (
                    <p className="text-sm text-muted-foreground">
                        Ediția aceasta nu are încă niciun nivel. Adaugă primul cu butonul de mai jos.
                    </p>
                )}

                <Button
                    className="touch-target min-h-12 w-full bg-magenta hover:bg-magenta-hover sm:w-auto"
                    disabled={!editionId || plin}
                    onClick={() => setEditing(null)}
                >
                    <Plus aria-hidden /> Nivel nou
                </Button>
                {plin && (
                    <p className="text-xs text-muted-foreground">
                        Ediția are deja {MAX_NIVELURI} niveluri. Editează unul existent.
                    </p>
                )}
                </>
                )}

                {editionId && editing !== undefined && (
                    <TierFormDialog
                        open
                        onOpenChange={(o) => { if (!o) setEditing(undefined); }}
                        editionId={editionId}
                        initial={editing ?? undefined}
                        onSubmit={(dto) => (dto.id ? updateTier.mutateAsync(dto) : createTier.mutateAsync(dto))}
                    />
                )}

                <ConfirmationDialog
                    isOpen={stergere.isOpen}
                    onClose={stergere.handleCancel}
                    onConfirm={stergere.handleConfirm}
                    title="Ștergi nivelul?"
                    description={
                        deSters
                            ? `„${deSters.name}" va dispărea din ediție. Dacă are deja revendicări, ștergerea va fi refuzată.`
                            : ''
                    }
                    confirmText="Șterge"
                    variant="destructive"
                    loading={deleteTier.isPending}
                />
            </div>
        </div>
    );
}
